import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  LineChart,
  Bell,
  Sparkles,
  ShieldCheck,
  RefreshCw,
  Activity,
  Building2,
  ArrowRight,
  CheckCircle2,
  Bot,
  Trophy,
  TrendingUp,
  Flame,
  Users,
} from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import { STS_LOGO } from "@/components/brand/Logo";
import Seo from "@/components/seo/Seo";

export default function SafeToSpend() {
  const { t } = useTranslation("products");
  const FEATURES = [
    { icon: Wallet, title: t("sts.features.safeToSpendTitle"), body: t("sts.features.safeToSpendBody") },
    { icon: Building2, title: t("sts.features.connectionsTitle"), body: t("sts.features.connectionsBody") },
    { icon: LineChart, title: t("sts.features.billsTitle"), body: t("sts.features.billsBody") },
    { icon: RefreshCw, title: t("sts.features.goalsTitle"), body: t("sts.features.goalsBody") },
    { icon: Sparkles, title: t("sts.features.insightsTitle"), body: t("sts.features.insightsBody") },
    { icon: Bell, title: t("sts.features.controlsTitle"), body: t("sts.features.controlsBody") },
  ];
  return (
    <>
      <Seo
        title={t("sts.seoTitle")}
        description={t("sts.seoDescription")}
        path="/safe-to-spend"
        preloadImages={[
          "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Safe to Spend 365",
            "operatingSystem": "iOS, Android",
            "applicationCategory": "FinanceApplication",
            "url": "https://atlasforgefinancial.com/safe-to-spend",
            "downloadUrl": [
              "https://apps.apple.com/us/app/safe-to-spend-365/id6792137232",
              "https://play.google.com/store/apps/details?id=com.atlasforge.safetospend",
            ],
            "installUrl": "https://apps.apple.com/us/app/safe-to-spend-365/id6792137232",
            "publisher": { "@type": "Organization", "name": "AtlasForge Financial" },
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Safe to Spend 365 Pro",
            "description":
              "Financial wellness subscription for Safe to Spend 365 — 14-day free trial, no credit card required.",
            "brand": { "@type": "Brand", "name": "Safe to Spend 365" },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "4.99",
              "highPrice": "9.99",
              "offerCount": 4,
              "availability": "https://schema.org/InStock",
            },
          },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-mint-glow pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <img
                src={STS_LOGO}
                alt="Safe to Spend 365"
                data-testid="sts-product-logo"
                width="480"
                height="480"
                decoding="async"
                fetchPriority="high"
                className="h-52 md:h-64 lg:h-72 w-auto object-contain mb-6"
              />
              <span
                data-testid="sts-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-[#00C48C]/30 bg-[#E6FAF4] px-3 py-1.5 text-[12px] font-semibold text-[#00A373]"
              >
                <span className="h-2 w-2 rounded-full bg-[#00C48C] af-pulse-dot" />
                {t("sts.eyebrow")}
              </span>
              <h1
                data-testid="sts-hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.02] text-[#0A1128]"
              >
                {t("sts.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-600 max-w-xl">
                {t("sts.heroBody")}
              </p>

              <div className="mt-9">
                <StoreBadges product="sts-hero" />
              </div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-slate-600">
                {[
                  "No budgeting categories required",
                  "Updates in real time",
                  "Plaid-secured bank connections",
                ].map((t) => (
                  <span key={t} className="inline-flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#00C48C]" /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero phone — real app screenshot */}
            <div className="lg:col-span-5 relative">
              <div className="mx-auto max-w-[340px]">
                <div
                  data-testid="sts-hero-phone"
                  className="relative rounded-[42px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10"
                >
                  <img
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp"
                    alt="Safe to Spend 365 Home screen — daily safe-to-spend number, buffer protection, and payday countdown"
                    fetchPriority="high"
                    decoding="async"
                    width="340"
                    height="737"
                    className="rounded-[36px] w-full block"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">How it works</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
              Three steps. No spreadsheets.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Securely connect accounts",
                body: "Link checking, savings, credit, loans, and investments through Plaid. We never see or store your bank credentials.",
              },
              {
                step: "02",
                title: "We do the math",
                body: "We analyze income, bills, subscriptions, debts, and savings goals to compute your real-time Safe to Spend 365.",
              },
              {
                step: "03",
                title: "Spend with confidence",
                body: "Get smart alerts before bills hit and AI-powered insights that explain where your money is actually going.",
              },
            ].map((s) => (
              <div key={s.step} className="rounded-2xl border border-slate-200 bg-white p-7 af-shadow-card">
                <p className="font-display text-[44px] leading-none text-[#00C48C]">{s.step}</p>
                <h3 className="font-display mt-4 text-[20px]">{s.title}</h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Key features</p>
              <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
                Everything you need for daily financial confidence.
              </h2>
            </div>
            <StoreBadges product="sts-features" />
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl bg-white border border-slate-200 p-6 af-shadow-card">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#E6FAF4]">
                  <Icon className="h-5 w-5 text-[#00A373]" />
                </span>
                <h3 className="font-display mt-4 text-[17px]">{title}</h3>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See it in action — real app screenshots */}
      <section id="screenshots" className="relative bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">See it in action</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
              Everything you need, in one calm place.
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
              Live captures from Safe to Spend 365 on iOS. Every number below
              comes from a real, Plaid-linked account — nothing simulated.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp",
                title: "Home",
                caption: "Today's Safe to Spend number, payday countdown, and momentum badge.",
                testid: "sts-shot-home",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/reg5tyj6_Screenshot%202.webp",
                title: "Accounts",
                caption: "Live net worth, 30-day trend, and full cash position across every linked account.",
                testid: "sts-shot-accounts",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/n0fazz8x_Screenshot%203.webp",
                title: "Goals & Plans",
                caption: "The 5-level Money Journey — Start → Safe → Control → Freedom → Wealth.",
                testid: "sts-shot-goals",
              },
              {
                src: "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/yppiivxk_Screenshot%204.webp",
                title: "Insights",
                caption: "Financial Health score, runway, and your Safe-to-Spend consistency streak.",
                testid: "sts-shot-insights",
              },
            ].map((s) => (
              <figure
                key={s.title}
                data-testid={s.testid}
                className="group"
              >
                <div className="relative rounded-[28px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10 group-hover:-translate-y-1 transition-transform">
                  <img
                    src={s.src}
                    alt={`Safe to Spend 365 ${s.title} screen`}
                    loading="lazy"
                    decoding="async"
                    className="rounded-[22px] w-full block"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-display text-[15px] text-[#0A1128]">{s.title}</p>
                  <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                    {s.caption}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Plans & pricing */}
      <section id="pricing" className="relative bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Plans &amp; pricing</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
              Try Pro free for 14 days. No credit card required.
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed">
              One number. Every dollar. Cancel anytime from the app.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00C48C]/10 text-[#008466] px-4 py-1.5 text-[12.5px] font-semibold">
              <CheckCircle2 className="h-3.5 w-3.5" />
              14-day free trial · No credit card required
            </div>
          </div>

          {/* Plan cards */}
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                id: "individual",
                name: "Individual",
                tagline: "For one person, one financial life.",
                monthly: "$4.99",
                yearly: "$39.00",
                yearlyBreakEven: "$3.25 / mo billed yearly",
                yearlySave: "Save 35% annually",
                icon: <Wallet className="h-5 w-5" />,
                perks: [
                  "Everything in Pro (see below)",
                  "Unlimited linked accounts",
                  "Unlimited Money Coach chats",
                  "All 5 Money Journey levels",
                ],
              },
              {
                id: "family",
                name: "Family",
                tagline: "For couples and households — up to 5 members.",
                monthly: "$9.99",
                yearly: "$79.00",
                yearlyBreakEven: "$6.58 / mo billed yearly",
                yearlySave: "Save 34% annually",
                icon: <Users className="h-5 w-5" />,
                perks: [
                  "Everything in Individual",
                  "Up to 5 household members",
                  "Shared goals & buffer",
                  "Household Financial Health score",
                ],
                highlighted: true,
              },
            ].map((plan) => (
              <div
                key={plan.id}
                data-testid={`sts-plan-${plan.id}`}
                className={`relative rounded-2xl border p-7 bg-white ${
                  plan.highlighted
                    ? "border-[#00C48C] shadow-xl ring-2 ring-[#00C48C]/15"
                    : "border-slate-200 af-shadow-card"
                }`}
              >
                {plan.highlighted && (
                  <span className="absolute -top-2.5 left-6 rounded-full bg-[#00C48C] text-white text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-0.5">
                    Best value
                  </span>
                )}
                <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0A1128] text-white">
                  {plan.icon}
                </div>
                <h3 className="mt-4 font-display text-[24px] text-[#0A1128]">
                  {plan.name}
                </h3>
                <p className="mt-1 text-[13.5px] text-slate-600">
                  {plan.tagline}
                </p>

                <div className="mt-6 flex items-baseline gap-2">
                  <p className="font-display text-[42px] leading-none text-[#0A1128]">
                    {plan.monthly}
                  </p>
                  <p className="text-[13px] text-slate-500">/ month</p>
                </div>
                <p className="mt-2 text-[13px] text-slate-600">
                  or{" "}
                  <strong className="text-[#0A1128]">{plan.yearly}</strong> /
                  year{" "}
                  <span className="text-[#008466] font-semibold">
                    ({plan.yearlySave})
                  </span>
                </p>
                <p className="text-[11.5px] text-slate-400">
                  {plan.yearlyBreakEven}
                </p>

                <ul className="mt-6 space-y-2.5">
                  {plan.perks.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-2 text-[13.5px] text-slate-700 leading-snug"
                    >
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#00C48C] shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex justify-center">
                  <StoreBadges product={`sts-plan-${plan.id}`} align="center" />
                </div>
              </div>
            ))}
          </div>

          {/* Pro features shared across plans */}
          <div className="mt-16 max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
            <div className="text-center max-w-xl mx-auto">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">
                Included with Pro
              </p>
              <h3 className="font-display mt-2 text-[26px] md:text-[32px] text-[#0A1128] leading-tight">
                Everything you get on every plan.
              </h3>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <Activity className="h-4 w-4" />,
                  title: "Safe to Spend Today",
                  body: "A live, single number that already respects your bills, savings, and buffer.",
                },
                {
                  icon: <Bot className="h-4 w-4" />,
                  title: "Ask the Money Coach",
                  body: "AI chat grounded in your real accounts — ask any money question, get plain answers.",
                },
                {
                  icon: <Trophy className="h-4 w-4" />,
                  title: "5-Level Money Journey",
                  body: "Start → Safe → Control → Freedom → Wealth. Unlock milestones as you progress.",
                },
                {
                  icon: <ShieldCheck className="h-4 w-4" />,
                  title: "Safety Buffer protection",
                  body: "A guarded reserve so an unexpected bill never wrecks the plan.",
                },
                {
                  icon: <TrendingUp className="h-4 w-4" />,
                  title: "Financial Health score",
                  body: "0-100 score that updates live and shows the exact next step to raise it.",
                },
                {
                  icon: <Flame className="h-4 w-4" />,
                  title: "Consistency streak",
                  body: "Track days you stayed within Safe-to-Spend — build a 30-day streak.",
                },
                {
                  icon: <LineChart className="h-4 w-4" />,
                  title: "Cash flow forecast &amp; runway",
                  body: "See what's coming, how many days you can go, and where to trim first.",
                },
                {
                  icon: <Bell className="h-4 w-4" />,
                  title: "Smart bill alerts",
                  body: "Warnings land before a bill hits so you never get caught out.",
                },
                {
                  icon: <Building2 className="h-4 w-4" />,
                  title: "Plaid-secured accounts",
                  body: "Link 12,000+ US institutions read-only. Your credentials never touch our servers.",
                },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3">
                  <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#00C48C]/10 text-[#008466] shrink-0">
                    {f.icon}
                  </div>
                  <div>
                    <p className="font-display text-[15px] text-[#0A1128]">
                      {f.title}
                    </p>
                    <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-center text-[12px] text-slate-500">
              After the 14-day trial, the plan you picked auto-starts. Cancel
              anytime in the app — no calls, no forms.
            </p>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">Security</p>
              <h2 className="font-display mt-3 text-[34px] md:text-[42px] leading-[1.05] text-[#0A1128]">
                Bank-grade security at every layer.
              </h2>
              <p className="mt-5 text-slate-600 max-w-lg">
                TLS in transit, industry-standard encryption at rest, and account
                connections powered by Plaid. AtlasForge Financial does not
                store your online banking usernames or passwords.
              </p>
              <Link
                to="/legal/security"
                data-testid="sts-link-security"
                className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#0A1128] hover:gap-3 transition-all"
              >
                Read our security overview <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-[#F7F9FC] p-8">
              {[
                { t: "Plaid-secured connectivity", d: "Customers connect via Plaid's trusted bank links." },
                { t: "Encrypted in transit & at rest", d: "TLS encryption for all traffic; AES-256 for sensitive data." },
                { t: "Continuous monitoring", d: "Automated detection for unusual access or transactions." },
                { t: "Least-privilege access", d: "Strict internal controls and MFA for all employees." },
              ].map((it) => (
                <div key={it.t} className="py-4 border-b border-slate-200 last:border-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#00A373]" />
                    <p className="font-display text-[16px]">{it.t}</p>
                  </div>
                  <p className="text-[13px] text-slate-600 mt-1">{it.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A1128] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28 text-center">
          <h2 className="font-display text-[36px] md:text-[48px] leading-[1.05]">
            Make every dollar a confident decision.
          </h2>
          <p className="mt-5 text-slate-300 max-w-xl mx-auto">
            Download Safe to Spend 365 and start with bank-grade clarity today.
          </p>
          <div className="mt-9 flex justify-center">
            <StoreBadges product="sts-bottom-cta" align="center" />
          </div>
        </div>
      </section>
    </>
  );
}
